import { Modal, generateResolver, yup } from "dyl-components";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Icon, Popup, Segment } from "semantic-ui-react";
import EditQuote from "./EditQuote";
import AddProducts from "./AddProducts";
import ProposalPreview from "./ProposalPreview";
import { useContext, useEffect, useState } from "react";
import { ACTIONS, CURRENT_STEPS, QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";
import { Error500 } from "pages";
import Order from "shared/forms/Order";
import Payment from "shared/Payment";
import { quoteSchema } from "shared/schemas/quote/quoteSchema";
import { orderSchema } from "shared/schemas/order/orderSchema";
import { checkoutSchema } from "shared/schemas/checkout/checkoutSchema";
import { getOrderDefaultValues, getQuoteDefaultValues } from "./helper";
import subscriptionActions from "actions/subscription";
import InvoicePreview from "./InvoicePreview";
import { upsellSchema } from "shared/schemas/upsell/upsellSchema";
import { addPlanRequirements, cartItemSchema, cartSchema } from "shared/schemas/cart/cartSchema";
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";

const ModalContent = ({
    opportunity_id,
    account_id,
    contact_id,
    isLinkedToCustomer,
    account_primary_contact_id,
    onCloseModal,
    header,
    isEditOrder,
}) => {
    const quote = useSelector((state) => state.quote.quote);
    const order = useSelector((state) => state.order.order);
    const isDyl = useSelector(state => state.auth.dyl_employee);
    const { account_term, plan_id } = useSelector((state) => {
        const { price_schedule, is_current_price_schedule, plan_id } = state.subscription.term || { term: null };
        if (is_current_price_schedule) {
            return { account_term: price_schedule, plan_id };
        } 
        return {};
    });

    const { quoteBuilderConfig } = useContext(QuoteBuilderContext);

    const { currentStep, id, isUpsell, action } = quoteBuilderConfig;

    const defaultValues = (() => {
        if (
            currentStep === CURRENT_STEPS.BUILD_QUOTE ||
            (currentStep === CURRENT_STEPS.ADD_PRODUCTS && id)
        ) {
            return getQuoteDefaultValues({ quote });
        }
        if ((currentStep === CURRENT_STEPS.ORDER || currentStep === CURRENT_STEPS.EDIT_ORDER) && id) {
            return getOrderDefaultValues({ order, account_id, account_term: isDyl ? account_term : null, action });
        }
        if (currentStep === CURRENT_STEPS.CHECKOUT) {
            return {
                ...getOrderDefaultValues({ order, account_id, account_term: isDyl ? account_term : null, action }),
                invoice_name: ""
            };
        }
        return {
            ...(action === ACTIONS.DEMO ? { term: "demo" } : {}),
            pricing_schedule: account_term,
            cart: [],
        };
    })();

    const currentStepSchema = (() => {
        if (currentStep === CURRENT_STEPS.BUILD_QUOTE) {
            return quoteSchema;
        }
        if (currentStep === CURRENT_STEPS.ORDER || currentStep === CURRENT_STEPS.EDIT_ORDER) {
            if (isDyl && !plan_id) {
                return {
                    ...orderSchema,
                    cart: addPlanRequirements(orderSchema.cart)
                }
            }
            return orderSchema;
        }
        if (currentStep === CURRENT_STEPS.CHECKOUT) {
            if (isDyl) {
                return checkoutSchema;
            }
            return {
                invoice_name: yup.string().nullable(true),
                custom_ach: yup.string().when("payment_method", {
                    is: "custom-ach",
                    then: (schema) => schema.required("This field is required"),
                }),
                term: yup.string().required("This field is required")
            };
        }
        if (isUpsell) {
            if (isDyl) {
                if (!plan_id) {
                    return {
                        cart: addPlanRequirements(upsellSchema.cart)
                    };
                }
                return {
                    cart: upsellSchema.cart.of(yup.object({
                        amount: cartItemSchema.amount
                    }))
                }
            }
            return upsellSchema;
        }
        if (isDyl) {
            if (!plan_id) {
                return {
                    cart: addPlanRequirements(cartSchema.cart)
                };
            }
            return {
                cart: cartSchema.cart.of(yup.object({
                    amount: cartItemSchema.amount
                }))
            }
        }
        return cartSchema;
    })();

    const methods = useForm({
        mode: "onChange",
        defaultValues: defaultValues,
        resolver: generateResolver(currentStepSchema),
    });

    const { trigger } = methods;

    useEffect(() => {
        if (currentStep === CURRENT_STEPS.BUILD_QUOTE || (currentStep === CURRENT_STEPS.ORDER || currentStep === CURRENT_STEPS.EDIT_ORDER) || currentStep === CURRENT_STEPS.ADD_PRODUCTS) {
            trigger(`cart`);
        }
    }, [currentStep, trigger]);

    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const isCurrentStepOrder = currentStep === CURRENT_STEPS.ORDER || currentStep === CURRENT_STEPS.EDIT_ORDER;
    const isCurrentStepQuote = currentStep === CURRENT_STEPS.BUILD_QUOTE;
    return (
        <>
            <Modal
                size={!isCurrentStepOrder && !isCurrentStepQuote ? "big" : "huge"}
                open
                onClose={() => {
                    methods.formState.isDirty
                        ? setIsConfirmationOpen(true)
                        : onCloseModal();
                }}
            >
                {header}
                <FormProvider {...methods}>
                    {isCurrentStepQuote && (
                        <EditQuote
                            account_id={account_id}
                            opportunity_id={opportunity_id}
                            isLinkedToCustomer={isLinkedToCustomer}
                            contact_id={contact_id}
                            account_primary_contact_id={
                                account_primary_contact_id
                            }
                        />
                    )}
                    {currentStep === CURRENT_STEPS.ADD_PRODUCTS && (
                        <AddProducts
                            opportunity_id={opportunity_id}
                            account_id={account_id}
                            contact_id={contact_id}
                        />
                    )}
                    {(isCurrentStepOrder) && (
                        <Order
                            account_id={account_id}
                            contact_id={contact_id}
                        />
                    )}
                    {currentStep === CURRENT_STEPS.CHECKOUT && (
                        <Payment account_id={account_id} isEditOrder={isEditOrder}/>
                    )}
                </FormProvider>
            </Modal>
            <FullScreenModalPopUp
                header={"Changes not saved"}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    onCloseModal();
                    setIsConfirmationOpen(false);
                    methods.reset();
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </>
    );
};

const QuoteBuilderModal = ({
    opportunity_id,
    account_id,
    contact_id,
    isLinkedToCustomer,
    account_primary_contact_id,
    isAdmin
}) => {
    const dispatch = useDispatch();
    const [isEditOrder, setIsEditOrder] = useState(false);
    useEffect(() => {
        if (account_id) {
            dispatch(subscriptionActions.getAccountTerm(account_id, isAdmin ? { isAdmin } : null));
        }
    }, [account_id, dispatch, isAdmin]);
    const account_id_created = useSelector(state => state.account.account_id_created)

    const {
        isReading,
        quote,
        quoteVersion,
        quoteVersions,
        isReadingQuoteVersions,
        quoteVersionError,
        orderError,
        order,
        invoice,
        invoiceError,
        opportunityQuoteOptions,
        isReadingOpportunityQuoteOptions,
    } = useSelector((state) => ({
        isReading:
            state.quote.quoteBeingRead ||
            state.quote.quoteVersionBeingRead ||
            state.order.orderBeingRead ||
            state.subscription.isReadingAccountTerm ||
            state.invoice.invoiceBeingRead,
        isReadingQuoteVersions: state.quote.quoteVersionsBeingRead,
        quote: state.quote.quote,
        quoteVersion: state.quote.quoteVersion,
        quoteVersions: state.quote.quoteVersions,
        quoteVersionError: state.quote.quoteVersionError,
        order: state.order.order,
        orderError: state.order.orderError,
        invoice: state.invoice.invoice,
        invoiceError: state.invoice.invoiceError,
        opportunityQuoteOptions: state.quote.opportunityQuoteOptions,
        isReadingOpportunityQuoteOptions:
            state.quote.isReadingOpportunityQuoteOptions,
    }));

    const { quoteBuilderConfig, onCloseModal, onViewProposal, onEditQuote } =
        useContext(QuoteBuilderContext);

    const { currentStep, id, isUpsell, action } = quoteBuilderConfig || {
        currentStep: null,
        id: null,
        isUpsell: false,
        action: null,
    };

    useEffect(() => {
        if (currentStep === CURRENT_STEPS.EDIT_ORDER) {
            setIsEditOrder(true);
        }
        if (currentStep === CURRENT_STEPS.ORDER) {
            setIsEditOrder(false);
        }
        if (!currentStep) {
            setIsEditOrder(false);
        }
    }, [currentStep]);

    const getModalHeader = () => {
        if (currentStep === CURRENT_STEPS.ADD_PRODUCTS) {
            return opportunity_id || action === ACTIONS.DEMO ? (
                "Add Products"
            ) : (
                <>
                    Product Catalog
                    {!isUpsell && (
                        <Popup
                            trigger={
                                <Icon
                                    size="small"
                                    style={{
                                        float: "right",
                                        marginRight: "1em",
                                        marginTop: "0.2em",
                                    }}
                                    className="fas fa-circle-info"
                                    color="primary"
                                />
                            }
                            content={
                                "Quotes can only be added to an opportunity"
                            }
                            inverted
                            position="bottom right"
                            size="mini"
                        />
                    )}
                </>
            );
        }
        if (currentStep === CURRENT_STEPS.BUILD_QUOTE) {
            return (
                <>
                    Quote Builder
                    {!isReadingOpportunityQuoteOptions && !isReading ?
                        opportunityQuoteOptions.length > 1 && (
                            <Dropdown
                                selection
                                options={opportunityQuoteOptions.map(
                                    (quote_id) => ({
                                        key: quote_id,
                                        value: quote_id,
                                        text: `Quote ID: ${quote_id}`,
                                    })
                                )}
                                value={id}
                                onChange={(_, { value }) => {
                                    onEditQuote(value, action);
                                }}
                                selectOnBlur={false}
                                style={{ float: "right", marginRight: "1em" }}
                            />
                        ) : (
                            <Icon size='small' color="black" style={{ float: "right", marginRight: "1em" }} className="fas fa-circle-notch" loading />
                        )}
                </>
            );
        }
        if (currentStep === CURRENT_STEPS.ORDER) {
            return "Create Order";
        }
        if (currentStep === CURRENT_STEPS.CHECKOUT) {
            return "Check Out";
        }
        if (currentStep === CURRENT_STEPS.EDIT_ORDER) {
            const order_status = order?.order_status;
            if (order_status === "completed" || order_status === "deleted") {
                return "View Order";
            }
            return "Edit Order";
        }
        if (currentStep === CURRENT_STEPS.PROPOSAL_PREVIEW) {
            if (quoteVersionError) {
                return "Proposal";
            }
            return (
                <>
                    {quoteVersion?.quote_information?.name}
                    {!isReadingQuoteVersions && quoteVersions.length > 1 && (
                        <Dropdown
                            selection
                            options={quoteVersions.map((version) => ({
                                key: version.id,
                                value: version.id,
                                text: version.name,
                            }))}
                            value={id}
                            onChange={(_, { value }) => {
                                onViewProposal(value);
                            }}
                            selectOnBlur={false}
                            style={{ float: "right", marginRight: "1em" }}
                        />
                    )}
                </>
            );
        }
        if (currentStep === CURRENT_STEPS.INVOICE) {
            if (invoiceError) {
                return "Invoice";
            }
            return invoice?.name || "Invoice";
        }
        return null;
    };

    const isOpen = Boolean(currentStep);

    if (!isOpen) {
        return null;
    }

    if (isReading || (currentStep === CURRENT_STEPS.BUILD_QUOTE && !Boolean(quote))) {
        return (
            <Modal
                size="big"
                open={isOpen}
                onClose={onCloseModal}
            >
                <Modal.Header>{getModalHeader()}</Modal.Header>
                <Modal.Content>
                    <Segment basic loading />
                </Modal.Content>
            </Modal>
        );
    }
    if (
        (currentStep === CURRENT_STEPS.PROPOSAL_PREVIEW && quoteVersionError) ||
        ((currentStep === CURRENT_STEPS.ORDER || currentStep === CURRENT_STEPS.EDIT_ORDER) && orderError) || 
        (currentStep === CURRENT_STEPS.INVOICE && invoiceError)
    ) {
        return (
            <Modal
                size="big"
                open={isOpen}
                onClose={onCloseModal}
            >
                <Modal.Header>{getModalHeader()}</Modal.Header>
                <Modal.Content>
                    <Error500 height="50vh" message={"Something went wrong"} />
                </Modal.Content>
            </Modal>
        );
    }
    if (currentStep === CURRENT_STEPS.PROPOSAL_PREVIEW) {
        return (
            <Modal
                size="big"
                open={isOpen}
                onClose={onCloseModal}
            >
                <Modal.Header>{getModalHeader()}</Modal.Header>
                <ProposalPreview opportunity_id={opportunity_id} />
            </Modal>
        );
    }
    if (currentStep === CURRENT_STEPS.INVOICE) {
        return (
            <Modal
                size="big"
                open={isOpen}
                onClose={onCloseModal}
            >
                <Modal.Header>{getModalHeader()}</Modal.Header>
                <InvoicePreview />
            </Modal>
        );
    }
    return (
        <ModalContent
            account_id={account_id || account_id_created}
            opportunity_id={opportunity_id}
            contact_id={contact_id}
            isLinkedToCustomer={isLinkedToCustomer}
            account_primary_contact_id={account_primary_contact_id}
            header={<Modal.Header>{getModalHeader()}</Modal.Header>}
            onCloseModal={onCloseModal}
            isEditOrder={isEditOrder}
            key={currentStep === CURRENT_STEPS.CHECKOUT}
        />
    );
};

export default QuoteBuilderModal;
